import { c as _c } from "react/compiler-runtime";
import type { PlaybookVersionNoGraph, VaultKind } from '@onefootprint/request-types/dashboard';
import { Divider, InlineAlert, MonacoProvider } from '@onefootprint/ui';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEventListener } from 'usehooks-ts';
import { useStore } from '@/components/playbook-builder';
import { useNodePanel } from '@/components/playbook-builder/components/node-panel/node-panel-context';
import useSidebar from '@/components/playbook-builder/hooks/use-sidebar';
import { onModEnter } from '@/components/playbook-builder/utils/on-mod-enter';
import { buildPrerequisiteTemplate } from '@/components/playbook-builder/utils/prerequisite-template';
import useOrgSession from '@/hooks/use-org-session';
import { DEFAULT_OB_DATA, TEST_MODE_NODE_ID } from '../../test-mode.constants';
import { generateTestExternalId } from '../test-form';
import { TestModeButton } from '../test-mode-button';
import { TestPanelHeader } from '../test-panel-header';
import { type EntityCreatorHandle, EntityCreatorSection } from './components/entity-creator-section';
import { RunActions, type RunActionsHandle } from './components/run-actions';
import { RunCurlPreview } from './components/run-curl-preview';
import { RunForm } from './components/run-form';
import type { OnboardingSection, RunFormValues } from './onboarding.types';
import { parseJson5Object, runsVerifiedPrefill } from './onboarding.utils';
type OnboardingTestModeProps = {
  selectedVersion: PlaybookVersionNoGraph;
  hasPendingChanges: boolean;
  hasErrors: boolean;
  hasHiddenErrors: boolean;
  onRevealHiddenErrors: () => void;
  // The ID of the in-progress draft version, run in place of the versioned key when there are
  // pending changes so test mode exercises the unsaved edits rather than the latest saved version.
  versionId?: string;
};
export const OnboardingTestMode = t0 => {
  const $ = _c(113);
  const {
    selectedVersion,
    hasPendingChanges,
    hasErrors,
    hasHiddenErrors,
    onRevealHiddenErrors,
    versionId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "testMode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    open: openSidebar,
    close: closeSidebar,
    isOpen: isSidebarOpen
  } = useSidebar();
  const {
    setDescriptor,
    bodySlot,
    footerSlot
  } = useNodePanel();
  let t2;
  if ($[1] !== isSidebarOpen) {
    t2 = isSidebarOpen(TEST_MODE_NODE_ID);
    $[1] = isSidebarOpen;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isOpen = t2;
  const {
    data: t3
  } = useStore();
  const {
    graph
  } = t3;
  const [savedEntity, setSavedEntity] = useState(null);
  const [activeSection, setActiveSection] = useState("entity-creator");
  const entityKind = selectedVersion.kind === "kyb" ? "business" : "person";
  const {
    sandbox
  } = useOrgSession();
  const entityCreatorRef = useRef(null);
  const runActionsRef = useRef(null);
  const savedFpId = savedEntity?.isSandbox === sandbox.isSandbox ? savedEntity.fpId : null;
  const isRunStepActive = !!savedFpId && activeSection === "run-form";
  let t4;
  if ($[3] !== bodySlot || $[4] !== footerSlot || $[5] !== isOpen || $[6] !== isRunStepActive) {
    t4 = event => {
      const target = event.target as Node;
      const isInPanel = target === document.body || !!bodySlot?.contains(target) || !!footerSlot?.contains(target);
      if (!isOpen || !isInPanel) {
        return;
      }
      onModEnter(() => isRunStepActive ? runActionsRef.current?.run() : entityCreatorRef.current?.submit())(event);
    };
    $[3] = bodySlot;
    $[4] = footerSlot;
    $[5] = isOpen;
    $[6] = isRunStepActive;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEventListener("keydown", t4, undefined, true);
  let t5;
  if ($[8] !== graph.nodes) {
    t5 = graph.nodes.some(_temp);
    $[8] = graph.nodes;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const hasPrerequisiteNode = t5;
  let prerequisites;
  let showVerifiedMatchTip;
  let t6;
  let t7;
  let t8;
  if ($[10] !== entityKind || $[11] !== graph || $[12] !== sandbox.isSandbox) {
    prerequisites = graph.nodes.flatMap(_temp2);
    showVerifiedMatchTip = entityKind === "person" && sandbox.isSandbox && runsVerifiedPrefill(graph);
    t8 = useForm;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = generateTestExternalId();
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    t7 = buildPrerequisiteTemplate(prerequisites) ?? DEFAULT_OB_DATA;
    $[10] = entityKind;
    $[11] = graph;
    $[12] = sandbox.isSandbox;
    $[13] = prerequisites;
    $[14] = showVerifiedMatchTip;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    prerequisites = $[13];
    showVerifiedMatchTip = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
  }
  let t9;
  if ($[19] !== t6 || $[20] !== t7) {
    t9 = {
      defaultValues: {
        onboardingExternalId: t6,
        prerequisiteData: t7,
        synchronousTimeoutSecs: 30
      }
    };
    $[19] = t6;
    $[20] = t7;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const form = t8(t9);
  let t10;
  if ($[22] !== form || $[23] !== hasPrerequisiteNode || $[24] !== prerequisites) {
    t10 = () => {
      if (!hasPrerequisiteNode) {
        return;
      }
      const current = form.getValues("prerequisiteData");
      let existingValues;
      try {
        existingValues = parseJson5Object(current);
      } catch {
        return;
      }
      const next = buildPrerequisiteTemplate(prerequisites, existingValues) ?? DEFAULT_OB_DATA;
      if (next !== current) {
        form.setValue("prerequisiteData", next);
      }
    };
    $[22] = form;
    $[23] = hasPrerequisiteNode;
    $[24] = prerequisites;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const t11 = graph?.id;
  let t12;
  if ($[26] !== t11) {
    t12 = [t11];
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  useEffect(t10, t12);
  let t13;
  if ($[28] !== closeSidebar || $[29] !== isOpen || $[30] !== openSidebar) {
    t13 = () => {
      if (isOpen) {
        closeSidebar();
      } else {
        openSidebar(TEST_MODE_NODE_ID);
      }
    };
    $[28] = closeSidebar;
    $[29] = isOpen;
    $[30] = openSidebar;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const toggle = t13;
  let t14;
  if ($[32] !== t) {
    t14 = t(_temp3);
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== entityKind || $[35] !== t) {
    t15 = entityKind === "business" ? t(_temp4) : t(_temp5);
    $[34] = entityKind;
    $[35] = t;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t14 || $[38] !== t15) {
    t16 = <TestPanelHeader title={t14} description={t15} docHref="https://docs.onefootprint.com/api-reference#post-onboardings" />;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== entityKind || $[41] !== hasPendingChanges || $[42] !== sandbox.isSandbox || $[43] !== t) {
    t17 = (!sandbox.isSandbox || hasPendingChanges) && <div className="flex flex-col gap-3 px-4 pt-4">{!sandbox.isSandbox && <InlineAlert variant="warning">{t($_2 => $_2.runMode.productionAlert[entityKind])}</InlineAlert>}{hasPendingChanges && <InlineAlert variant="neutral">{t(_temp6)}</InlineAlert>}</div>;
    $[40] = entityKind;
    $[41] = hasPendingChanges;
    $[42] = sandbox.isSandbox;
    $[43] = t;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== sandbox.isSandbox) {
    t18 = fpId => setSavedEntity({
      fpId,
      isSandbox: sandbox.isSandbox
    });
    $[45] = sandbox.isSandbox;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const t19 = activeSection === "entity-creator";
  let t20;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = editing => setActiveSection(editing ? "entity-creator" : "run-form");
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== entityKind || $[49] !== savedFpId || $[50] !== showVerifiedMatchTip || $[51] !== t18 || $[52] !== t19) {
    t21 = <EntityCreatorSection ref={entityCreatorRef} entityKind={entityKind} savedFpId={savedFpId} setSavedFpId={t18} isEditing={t19} setIsEditing={t20} showVerifiedMatchTip={showVerifiedMatchTip} />;
    $[48] = entityKind;
    $[49] = savedFpId;
    $[50] = showVerifiedMatchTip;
    $[51] = t18;
    $[52] = t19;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  const t22 = !!savedFpId;
  let t23;
  if ($[54] !== entityKind || $[55] !== graph || $[56] !== hasPrerequisiteNode || $[57] !== isRunStepActive || $[58] !== selectedVersion || $[59] !== t22) {
    t23 = <RunForm isExpanded={isRunStepActive} hasSavedEntity={t22} hasPrerequisiteNode={hasPrerequisiteNode} entityKind={entityKind} playbook={selectedVersion} graph={graph} />;
    $[54] = entityKind;
    $[55] = graph;
    $[56] = hasPrerequisiteNode;
    $[57] = isRunStepActive;
    $[58] = selectedVersion;
    $[59] = t22;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== t21 || $[62] !== t23) {
    t24 = <MonacoProvider languageName="template"><div className="flex flex-col gap-4 px-4 my-4">{t21}{t23}</div></MonacoProvider>;
    $[61] = t21;
    $[62] = t23;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== t16 || $[65] !== t17 || $[66] !== t24) {
    t25 = <>{t16}{t17}{t24}</>;
    $[64] = t16;
    $[65] = t17;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  const body = t25;
  let t26;
  if ($[68] !== hasPendingChanges || $[69] !== savedFpId || $[70] !== selectedVersion || $[71] !== versionId) {
    t26 = <RunCurlPreview savedFpId={savedFpId} playbook={selectedVersion} hasPendingChanges={hasPendingChanges} versionId={versionId} />;
    $[68] = hasPendingChanges;
    $[69] = savedFpId;
    $[70] = selectedVersion;
    $[71] = versionId;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Divider className="-mx-3 !w-[calc(100%+1.5rem)]" />;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== closeSidebar) {
    t28 = () => closeSidebar();
    $[74] = closeSidebar;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  let t29;
  if ($[76] !== entityKind || $[77] !== hasErrors || $[78] !== hasHiddenErrors || $[79] !== hasPendingChanges || $[80] !== onRevealHiddenErrors || $[81] !== savedFpId || $[82] !== selectedVersion || $[83] !== t28 || $[84] !== versionId) {
    t29 = <RunActions ref={runActionsRef} savedFpId={savedFpId} entityKind={entityKind} playbook={selectedVersion} hasErrors={hasErrors} hasHiddenErrors={hasHiddenErrors} onRevealHiddenErrors={onRevealHiddenErrors} hasPendingChanges={hasPendingChanges} versionId={versionId} onClose={t28} />;
    $[76] = entityKind;
    $[77] = hasErrors;
    $[78] = hasHiddenErrors;
    $[79] = hasPendingChanges;
    $[80] = onRevealHiddenErrors;
    $[81] = savedFpId;
    $[82] = selectedVersion;
    $[83] = t28;
    $[84] = versionId;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== t26 || $[87] !== t29) {
    t30 = <div className="flex flex-col gap-4 w-full max-md:min-w-0 max-md:max-h-[55dvh] max-md:overflow-y-auto max-md:-mx-3 max-md:w-[calc(100%+1.5rem)] max-md:px-3">{t26}{t27}{t29}</div>;
    $[86] = t26;
    $[87] = t29;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  const footer = t30;
  let t31;
  let t32;
  if ($[89] !== closeSidebar || $[90] !== isOpen || $[91] !== setDescriptor) {
    t31 = () => {
      if (!isOpen) {
        return;
      }
      setDescriptor({
        nodeId: TEST_MODE_NODE_ID,
        canEdit: true,
        hasCustomFooter: true,
        size: "lg",
        anchorless: true,
        isDirty: false,
        clearDirty: _temp7,
        onClose: closeSidebar
      });
      return () => setDescriptor(_temp8);
    };
    t32 = [isOpen, setDescriptor, closeSidebar];
    $[89] = closeSidebar;
    $[90] = isOpen;
    $[91] = setDescriptor;
    $[92] = t31;
    $[93] = t32;
  } else {
    t31 = $[92];
    t32 = $[93];
  }
  useEffect(t31, t32);
  let t33;
  if ($[94] !== t) {
    t33 = t(_temp9);
    $[94] = t;
    $[95] = t33;
  } else {
    t33 = $[95];
  }
  let t34;
  if ($[96] !== selectedVersion || $[97] !== t33 || $[98] !== toggle) {
    t34 = <TestModeButton label={t33} onClick={toggle} selectedVersion={selectedVersion} />;
    $[96] = selectedVersion;
    $[97] = t33;
    $[98] = toggle;
    $[99] = t34;
  } else {
    t34 = $[99];
  }
  let t35;
  if ($[100] !== body || $[101] !== bodySlot || $[102] !== isOpen) {
    t35 = isOpen && bodySlot ? createPortal(body, bodySlot) : null;
    $[100] = body;
    $[101] = bodySlot;
    $[102] = isOpen;
    $[103] = t35;
  } else {
    t35 = $[103];
  }
  let t36;
  if ($[104] !== footer || $[105] !== footerSlot || $[106] !== isOpen) {
    t36 = isOpen && footerSlot ? createPortal(footer, footerSlot) : null;
    $[104] = footer;
    $[105] = footerSlot;
    $[106] = isOpen;
    $[107] = t36;
  } else {
    t36 = $[107];
  }
  let t37;
  if ($[108] !== form || $[109] !== t34 || $[110] !== t35 || $[111] !== t36) {
    t37 = <FormProvider {...form}>{t34}{t35}{t36}</FormProvider>;
    $[108] = form;
    $[109] = t34;
    $[110] = t35;
    $[111] = t36;
    $[112] = t37;
  } else {
    t37 = $[112];
  }
  return t37;
};
function _temp(node) {
  return node.kind === "prerequisite";
}
function _temp2(node_0) {
  return node_0.kind === "prerequisite" ? node_0.prerequisites : [];
}
function _temp3($) {
  return $.runMode.title;
}
function _temp4($_0) {
  return $_0.runMode.businessDescription;
}
function _temp5($_1) {
  return $_1.runMode.description;
}
function _temp6($_3) {
  return $_3.runMode.pendingChangesAlert;
}
function _temp7() {}
function _temp8(current_0) {
  return current_0?.nodeId === TEST_MODE_NODE_ID ? null : current_0;
}
function _temp9($_4) {
  return $_4.title;
}